// /////////////////////////////////////////////////////////////
// [ファイル] useAssistantSessions.ts
// [概要] Workspace アシスタントの会話セッションを Firestore に永続化するフック。
//        ChatGPT 風のサイドバー (一覧・新規作成・切替・削除) を支える。
//        メッセージは各セッションの messages サブコレクションに保存する
//        (Markdown 長文でも 1MiB 制限・全体再書込を避けるため)。
// /////////////////////////////////////////////////////////////
import { useEffect, useState, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../firebase/AuthProvider';

const SESSIONS = 'HozenallCloud_Assistant_Sessions';

// ⊕ 添付ファイル (PDF/Office/テキスト) の表示・永続化単位。
// url は Storage 保存後の永続 URL (保存失敗時は名前のみのチップ表示になる)
export interface AssistantFileAttachment {
  name: string;
  url?: string;
}

export interface AssistantMessage {
  role: 'user' | 'model';
  text: string;
  // 表示用の画像 URL。送信時の貼り付け画像 (data URL の即時表示) と、
  // Storage 保存後の永続 URL (image_urls) の両方がここに入りうる
  images?: string[];
  // Storage へ保存した画像の永続 URL。Firestore に保存し、再読込・履歴で再表示する
  imageUrls?: string[];
  // ⊕ 添付ファイル。Firestore (file_attachments) に保存し、履歴でチップ表示する
  files?: AssistantFileAttachment[];
  // 発話日時 (エポックms)。履歴としてバックエンドへ渡し、AI が鮮度を判断する材料にする
  at?: number;
  // トリガー実行の処理状態 (queued=受付済 processing=推論中 done=完了 error=失敗 cancelled=停止ボタンで中断)。
  // HTTP 方式の発話には付かない。フロントは queued/processing を「応答待ち」表示に使う
  status?: 'queued' | 'processing' | 'done' | 'error' | 'cancelled';
  // Firestore メッセージ doc の id (回答への評価 👍👎 の紐付けに使う)
  id?: string;
  // AI 回答でそのターンに実行したツール名 (バックエンドが tool_names として保存)
  toolNames?: string[];
  // メール自動処理が作った発話の元メール情報 (mail_source)。あれば本文の代わりにカードを出す
  mailSource?: AssistantMailSource;
}

// アシスタントの種別。会話一覧を用途別 (GWS / 会計) に分離するためにセッションへ付与する。
// 既存セッション (タグ無し) はクライアント側で 'gws' とみなす。
export type AgentType = 'gws' | 'accounting';

export interface AssistantSession {
  id: string;
  title: string;
  updatedAt: number; // ソート用にミリ秒へ変換 (未確定時は 0)
  // 所属グループ id。空は「共通」。バックエンドはこの値で注入する定義を「共通 + グループ」に絞る
  groupId: string;
  // 会話の起点。'gmail' = メール自動処理が作った会話 (一覧に自動の印を出す)。未設定は画面起点
  origin?: string;
}

/** メール自動処理が作った発話に付く、元メールの情報 (会話の先頭にカードで表示する) */
export interface AssistantMailSource {
  from: string;
  subject: string;
  date: string;
  attachments: string[];
  gmailUrl: string;
}

function toMillis(value: unknown): number {
  return value instanceof Timestamp ? value.toMillis() : 0;
}

/** 最初の発話から暫定タイトル (先頭24文字) を作る。AI が後で正式タイトルへ上書きする */
function provisionalTitle(firstMessage: string): string {
  const t = firstMessage.trim().replace(/\s+/g, ' ');
  return t.length > 24 ? `${t.slice(0, 24)}…` : t || '新しいチャット';
}

export const useAssistantSessions = (agentType: AgentType = 'gws') => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<AssistantSession[]>([]);
  const [loading, setLoading] = useState(true);

  // 本人のセッションを購読。複合インデックス回避のため where のみ + クライアント側で
  // agent_type フィルタ + ソート。タグ無し (旧データ) は 'gws' とみなす。
  useEffect(() => {
    if (!user?.email) {
      setSessions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, SESSIONS), where('user_id', '==', user.email)),
      (snap) => {
        const list = snap.docs
          .filter((d) => ((d.data().agent_type as AgentType) ?? 'gws') === agentType)
          .map((d) => ({
            id: d.id,
            title: (d.data().title as string) || '新しいチャット',
            updatedAt: toMillis(d.data().updated_at),
            groupId: typeof d.data().group_id === 'string' ? (d.data().group_id as string) : '',
            origin: typeof d.data().origin === 'string' ? (d.data().origin as string) : undefined,
          }))
          .sort((a, b) => b.updatedAt - a.updatedAt);
        setSessions(list);
        setLoading(false);
      },
      (err) => {
        console.error('セッション購読に失敗しました', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.email, agentType]);

  // 新規セッションを作成し、その id を返す。種別 (agent_type) を付与して一覧を分離し、
  // グループ id (空は共通) を付与してバックエンドの定義注入を絞る
  const createSession = useCallback(
    async (firstMessage: string, groupId = ''): Promise<string | null> => {
      if (!user?.email) return null;
      const ref = await addDoc(collection(db, SESSIONS), {
        user_id: user.email,
        title: provisionalTitle(firstMessage),
        agent_type: agentType,
        group_id: groupId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      return ref.id;
    },
    [user?.email, agentType]
  );

  // セッションの所属グループを変更する (空で共通へ戻す)。以降の発話から注入が切り替わる
  const setSessionGroup = useCallback(
    async (sessionId: string, groupId: string): Promise<void> => {
      await updateDoc(doc(db, SESSIONS, sessionId), { group_id: groupId });
    },
    []
  );

  // セッションと配下の messages を削除する
  const deleteSession = useCallback(
    async (sessionId: string): Promise<void> => {
      if (!user?.email) return;
      // list 取得もルール (user_id 一致) を満たすよう where で制約する
      const msgsSnap = await getDocs(
        query(
          collection(db, SESSIONS, sessionId, 'messages'),
          where('user_id', '==', user.email)
        )
      );
      // writeBatch は 1 回 500 件まで。超える場合は分割してコミットする
      const docs = msgsSnap.docs;
      for (let i = 0; i < docs.length; i += 500) {
        const batch = writeBatch(db);
        docs.slice(i, i + 500).forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
      await deleteDoc(doc(db, SESSIONS, sessionId));
    },
    [user?.email]
  );

  // セッションのタイトルを更新する (AI 生成タイトルの反映に使う)
  const renameSession = useCallback(
    async (sessionId: string, title: string): Promise<void> => {
      await updateDoc(doc(db, SESSIONS, sessionId), { title });
    },
    []
  );

  return { sessions, loading, createSession, deleteSession, renameSession, setSessionGroup };
};

// --- messages サブコレクションのヘルパー (useWorkspaceAssistant から利用) -------

/** セッションのメッセージを時系列 (昇順) で読み込む */
export async function loadSessionMessages(
  sessionId: string,
  userEmail: string
): Promise<AssistantMessage[]> {
  // list 取得もルール (user_id 一致) を満たすよう where で制約する
  const snap = await getDocs(
    query(collection(db, SESSIONS, sessionId, 'messages'), where('user_id', '==', userEmail))
  );
  return snap.docs
    .map((d) => ({
      role: d.data().role as 'user' | 'model',
      text: (d.data().text as string) ?? '',
      _at: toMillis(d.data().created_at),
    }))
    .sort((a, b) => a._at - b._at)
    .map(({ role, text, _at }) => ({ role, text, at: _at || undefined }));
}

/** メッセージを 1 件追記し、セッションの updated_at を更新する。追記したメッセージの id を返す
 *  extraFields はトリガー実行用の付加情報 (status='queued'・mode など) を想定 */
export async function appendSessionMessage(
  sessionId: string,
  userEmail: string,
  msg: AssistantMessage,
  extraFields?: Record<string, unknown>
): Promise<string> {
  const added = await addDoc(collection(db, SESSIONS, sessionId, 'messages'), {
    user_id: userEmail, // ルール簡略化のため親と同値を複製
    role: msg.role,
    text: msg.text,
    created_at: serverTimestamp(),
    ...(extraFields ?? {}),
    // Storage 保存済みの画像 URL があれば永続化 (再読込・履歴で再表示する)
    ...(msg.imageUrls && msg.imageUrls.length > 0 ? { image_urls: msg.imageUrls } : {}),
    // 添付ファイル (名前 + Storage URL)。Firestore は undefined を弾くため url は有る時のみ持たせる
    ...(msg.files && msg.files.length > 0
      ? {
          file_attachments: msg.files.map((f) => ({
            name: f.name,
            ...(f.url ? { url: f.url } : {}),
          })),
        }
      : {}),
  });
  await updateDoc(doc(db, SESSIONS, sessionId), { updated_at: serverTimestamp() });
  return added.id;
}
